/* CPG YMM widget v2 — Convermax-teardown Steps 3+5.
 *
 * Renders "Select Your Vehicle" cascades (Year → Make → Model → Submodel →
 * Engine) into EVERY [data-cpg-ymm-widget] host on the page, backed by our
 * public fitment API. The chosen vehicle is saved to localStorage (the garage)
 * and exposed via window.CPGFitment — the adapter consumed by
 * cpg-product-main.js / cpg-fitment-icons.js for badges and fit cards.
 *
 * v2 additions (Step 5): multi-instance support (homepage hero + PDP + modal),
 * a light style variant (data-cpg-ymm-style="light") for the white hero card,
 * CPGFitment.openPicker() — a modal picker the header garage button opens —
 * and [data-cpg-garage-button] decoration (label shows the saved vehicle).
 * Instances stay in sync via the cpg:vehicle-changed event.
 *
 * Dependency-free ES5-ish; fails soft: if the API is unreachable the selects
 * stay empty and the page behaves like "no vehicle selected".
 */
(function () {
  'use strict';
  if (window.__cpgYmmWidgetInit) return; // double-include guard
  window.__cpgYmmWidgetInit = true;

  var GARAGE_KEY = 'cpg:garage:vehicle'; // the ACTIVE vehicle (back-compat: PDP consumers read this)
  var GARAGE_LIST_KEY = 'cpg:garage:list'; // all saved vehicles
  var API_BASE_FALLBACK = 'https://carpartglobal-api-743237931312.us-central1.run.app/storefront/fitment';
  /** The storewide "all parts for your vehicle" page (Shopify ignores S&D
   *  filters on collections over 5k products, so /collections/all can't). */
  // Owner 2026-07-23: vehicle picks land on the faceted Shop All scoped to the
  // vehicle (was the /pages/shop-by-vehicle results page — still menu-linked).
  var VEHICLE_RESULTS_PATH = '/pages/shop-all';
  function vehicleResultsUrl(v) {
    if (!v || !v.year || !v.make || !v.model) return VEHICLE_RESULTS_PATH;
    return VEHICLE_RESULTS_PATH + '?year=' + encodeURIComponent(v.year) +
      '&make=' + encodeURIComponent(v.make) + '&model=' + encodeURIComponent(v.model);
  }

  function vehicleLabel(v) {
    return [v.year, v.make, v.model, v.submodel, v.engine].filter(Boolean).join(' ');
  }
  function vehicleId(v) { return vehicleLabel(v); } // label is unique per distinct vehicle

  /** The active vehicle — what the PDP badge, header chip, and collection filter use. */
  function readGarage() {
    try {
      var raw = window.localStorage.getItem(GARAGE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      return v && v.year && v.make && v.model ? v : null;
    } catch (e) { return null; }
  }

  /** Every saved vehicle, newest first. Migrates a legacy lone active vehicle in. */
  function readGarageList() {
    var list = [];
    try {
      var raw = window.localStorage.getItem(GARAGE_LIST_KEY);
      if (raw) list = JSON.parse(raw) || [];
    } catch (e) { list = []; }
    if (!list.length) {
      var active = readGarage();
      if (active) { list = [active]; try { window.localStorage.setItem(GARAGE_LIST_KEY, JSON.stringify(list)); } catch (e) {} }
    }
    return list.filter(function (v) { return v && v.year && v.make && v.model; });
  }

  function persistList(list) {
    try { window.localStorage.setItem(GARAGE_LIST_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function fireChanged() { try { window.dispatchEvent(new CustomEvent('cpg:vehicle-changed')); } catch (e) {} }

  /** Set (or clear, with null) the active vehicle. Does not touch the saved list. */
  function setActiveVehicle(v) {
    try {
      if (v) window.localStorage.setItem(GARAGE_KEY, JSON.stringify(v));
      else window.localStorage.removeItem(GARAGE_KEY);
    } catch (e) {}
    fireChanged();
  }

  /** Add a vehicle to the garage (dedup by label) and make it active. */
  function addToGarage(v) {
    if (!v) return;
    v.label = v.label || vehicleLabel(v);
    var list = readGarageList();
    var id = vehicleId(v);
    list = list.filter(function (e) { return vehicleId(e) !== id; }); // drop any dup
    list.unshift(v); // newest first
    if (list.length > 8) list = list.slice(0, 8); // sane cap
    persistList(list);
    setActiveVehicle(v);
  }

  /** Remove a saved vehicle; if it was active, promote the next one (or clear). */
  function removeFromGarage(id) {
    var list = readGarageList().filter(function (e) { return vehicleId(e) !== id; });
    persistList(list);
    var active = readGarage();
    if (active && vehicleId(active) === id) setActiveVehicle(list[0] || null);
    else fireChanged();
  }

  // Back-compat shim: old call sites use writeGarage(v)/writeGarage(null).
  function writeGarage(vehicle) { if (vehicle) addToGarage(vehicle); else setActiveVehicle(null); }

  function apiBase() {
    var host = document.querySelector('[data-cpg-ymm-widget][data-cpg-api-base]');
    return (host && host.getAttribute('data-cpg-api-base')) || API_BASE_FALLBACK;
  }

  // ── adapter consumed by cpg-product-main.js / cpg-fitment-icons.js ─────────
  window.CPGFitment = {
    isReady: function () { return true; },
    hasVehicle: function () { return Boolean(readGarage()); },
    getVehicle: readGarage,
    getVehicleText: function () {
      var v = readGarage();
      return v ? vehicleLabel(v) : '';
    },
    clearVehicle: function () {
      // Owner 2026-07-24 (Option A): reset = REMOVE. Drop the active vehicle
      // from the saved list too and leave NO active vehicle — otherwise the
      // header re-adopts it from the list and reset appears to do nothing.
      var active = readGarage();
      if (active) {
        var id = vehicleId(active);
        persistList(readGarageList().filter(function (e) { return vehicleId(e) !== id; }));
      }
      setActiveVehicle(null);
    },
    /** Save a complete vehicle (year+make+model required) to the garage and make
     *  it active — used by the makes-index year-completer landing. */
    saveVehicle: function (v) {
      if (!v || !v.year || !v.make || !v.model) return false;
      writeGarage({ year: parseInt(v.year, 10), make: v.make, model: v.model, submodel: v.submodel || null, engine: v.engine || null });
      return true;
    },
    openPicker: function () { openPickerModal(); },
    /** Resolve 'fit' | 'not-fit' | 'no-data' | 'fit-check-engine' | 'error' for a
     *  product id — now via the universe-aware fitment ENGINE (/fit, spec §3/§4),
     *  so the PDP agrees exactly with the collection badges and never over-asks
     *  about engine on a single-engine vehicle. State map: FITS/FITS_WITH_NOTES →
     *  fit · NEEDS_QUALIFIER(engine) → fit-check-engine · NO_FIT → not-fit ·
     *  UNIVERSAL/UNKNOWN → no-data (neutral, never red). */
    verify: function (productId) {
      var v = readGarage();
      if (!v || !productId) return Promise.resolve('error');
      var qs = 'pid=' + encodeURIComponent(productId) +
        '&year=' + encodeURIComponent(v.year) +
        '&make=' + encodeURIComponent(v.make) +
        '&model=' + encodeURIComponent(v.model) +
        (v.submodel ? '&submodel=' + encodeURIComponent(v.submodel) : '') +
        (v.engine ? '&engine=' + encodeURIComponent(v.engine) : '');
      return fetch(apiBase() + '/fit?' + qs)
        .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('http ' + r.status)); })
        .then(function (d) {
          var s = d.state;
          window.CPGFitment.fitNotes = null; // cleared each verify; set only on FITS_WITH_NOTES
          if (s === 'NEEDS_QUALIFIER' && d.ask === 'engine' && d.options && d.options.length) {
            // Option B (owner 2026-08-18): expose EVERY engine the car came with (with its fits flag),
            // not just the fitting ones — the picker lists them all and tells the truth after the
            // shopper picks theirs (green if it fits, red + "see the parts that fit" if it does not).
            window.CPGFitment.engineAsk = { options: d.options };
            return 'fit-check-engine';
          }
          window.CPGFitment.engineAsk = null;
          // Carry the curated fit notes through so the PDP confirmed-fit card can
          // show the same "— For TH-350 Transmission" caveat the collection card does.
          if (s === 'FITS_WITH_NOTES' && Array.isArray(d.notes) && d.notes.length) {
            window.CPGFitment.fitNotes = d.notes;
          }
          if (s === 'FITS' || s === 'FITS_WITH_NOTES') return 'fit';
          if (s === 'NO_FIT') return 'not-fit';
          if (s === 'UNIVERSAL') return 'universal';   // not vehicle-keyed, buy gate OPEN
          if (s === 'BUILD_SPEC') return 'build-spec'; // engine-build, sold by spec, gate OPEN
          return 'no-data'; // UNKNOWN → blue "unverified", gate stays gated
        })
        .catch(function () { return 'error'; });
    },
    /** Set the engine on the ACTIVE garage vehicle (PDP engine-ask answer) —
     *  replaces the saved entry so the label/id stay consistent. */
    setEngine: function (engine) {
      var v = readGarage();
      if (!v) return false;
      var oldId = vehicleId(v);
      v.engine = engine || null;
      v.label = vehicleLabel(v);
      var list = readGarageList().filter(function (e) { return vehicleId(e) !== oldId && vehicleId(e) !== vehicleId(v); });
      list.unshift(v);
      persistList(list);
      setActiveVehicle(v);
      return true;
    }
  };

  // ── shared CSS (dark default + light variant + modal) ──────────────────────
  function injectCss() {
    if (document.getElementById('cpg-ymm-css')) return;
    var style = document.createElement('style');
    style.id = 'cpg-ymm-css';
    style.textContent =
      '.cpg-ymm{display:grid;grid-template-columns:1fr 1fr;gap:10px}' +
      '.cpg-ymm select{width:100%;padding:11px 12px;border-radius:8px;border:1px solid rgba(255,255,255,.25);background:rgba(255,255,255,.06);color:inherit;font:inherit}' +
      '.cpg-ymm select:disabled{opacity:.45}' +
      '.cpg-ymm select option{color:#111;background:#fff}' +
      '.cpg-ymm__actions{grid-column:1/-1;display:flex;gap:10px}' +
      '.cpg-ymm__go{flex:1;padding:12px;border:0;border-radius:8px;background:#e31b23;color:#fff;font-weight:700;cursor:pointer}' +
      '.cpg-ymm__go:hover{background:#c2151d}' +
      '.cpg-ymm__go:disabled{background:#e5e7eb;color:#9aa0aa;cursor:default}' +
      '.cpg-ymm__reset{padding:12px 16px;border:1px solid rgba(255,255,255,.3);border-radius:8px;background:transparent;color:inherit;cursor:pointer}' +
      '.cpg-ymm__confirm{grid-column:1/-1;margin:0;padding:10px 12px;border-radius:8px;background:rgba(39,174,96,.15);color:inherit;font-weight:600;display:none}' +
      '.cpg-ymm--saved .cpg-ymm__confirm{display:block}' +
      /* light variant (white hero card) */
      '[data-cpg-ymm-style="light"] .cpg-ymm select{border:1px solid #d5d9e0;background:#fff;color:#172231}' +
      '[data-cpg-ymm-style="light"] .cpg-ymm__reset{border:1px solid #cbd2da;color:#172231}' +
      '[data-cpg-ymm-style="light"] .cpg-ymm__confirm{background:#e7f4ec;color:#15803d}' +
      /* labeled mode (screen 07 hero) — exact values from docs/mockups/v5.2/styles.css */
      '.cpg-ymm--labeled{gap:14px 16px}' +
      '.cpg-ymm__field{display:flex;flex-direction:column;gap:7px;min-width:0}' +
      '.cpg-ymm__flabel{font-family:var(--cpg-font-mono,ui-monospace,monospace);font-size:10px;font-weight:500;letter-spacing:.16em;text-transform:uppercase;color:#8b9099}' +
      '.cpg-ymm--labeled select{border-radius:10px;padding:12px;font-size:15px}' +
      '.cpg-ymm--labeled select:not(:disabled){border:1px solid #101114;color:#101114;font-weight:700}' +
      '.cpg-ymm--labeled select:disabled{border:1px solid #d5d9e0;color:#9aa0aa;opacity:1;font-weight:600}' +
      /* big red CTA: Barlow Condensed 700 22px uppercase + 3px solid press-shadow */
      '.cpg-ymm--labeled .cpg-ymm__go{font-family:var(--cpg-font-display,"Barlow Condensed",sans-serif);font-size:22px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;padding:14px;border-radius:10px;box-shadow:0 3px 0 #a5121a}' +
      '.cpg-ymm--labeled .cpg-ymm__go:active{transform:translateY(2px);box-shadow:0 1px 0 #a5121a}' +
      '.cpg-ymm--labeled .cpg-ymm__go:disabled{box-shadow:none;transform:none}' +
      '.cpg-ymm--labeled .cpg-ymm__reset{border-radius:10px}' +
      '.cpg-ymm__go-arrow{margin-left:4px}' +
      /* modal */
      '.cpg-ymm-modal{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px}' +
      '.cpg-ymm-modal__backdrop{position:absolute;inset:0;background:rgba(10,14,20,.6)}' +
      '.cpg-ymm-modal__card{position:relative;width:100%;max-width:460px;background:#141a22;color:#fff;border-radius:14px;padding:22px;box-shadow:0 20px 60px rgba(0,0,0,.4)}' +
      '.cpg-ymm-modal__title{margin:0 0 4px;font-size:18px;font-weight:800}' +
      '.cpg-ymm-modal__sub{margin:0 0 16px;font-size:13px;opacity:.75}' +
      '.cpg-ymm-modal__close{position:absolute;top:12px;right:14px;border:0;background:transparent;color:inherit;font-size:22px;cursor:pointer;line-height:1}' +
      /* garage list */
      '.cpg-garage-list{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}' +
      '.cpg-garage-item{display:flex;align-items:stretch;gap:6px;border:1px solid rgba(255,255,255,.16);border-radius:10px;overflow:hidden}' +
      '.cpg-garage-item.is-active{border-color:#e02b2b;background:rgba(224,43,43,.10)}' +
      '.cpg-garage-item__pick{flex:1;display:flex;align-items:center;gap:10px;padding:12px 14px;background:transparent;border:0;color:inherit;font:inherit;text-align:left;cursor:pointer}' +
      '.cpg-garage-item__dot{width:12px;height:12px;border-radius:50%;border:2px solid rgba(255,255,255,.4);flex:0 0 auto}' +
      '.cpg-garage-item.is-active .cpg-garage-item__dot{border-color:#e02b2b;background:#e02b2b}' +
      '.cpg-garage-item__label{flex:1;font-weight:600}' +
      '.cpg-garage-item__tag{font-size:11px;font-weight:700;letter-spacing:.03em;color:#e58a8a;text-transform:uppercase}' +
      '.cpg-garage-item__remove{border:0;background:transparent;color:inherit;opacity:.55;font-size:20px;padding:0 14px;cursor:pointer}' +
      '.cpg-garage-item__remove:hover{opacity:1}' +
      '.cpg-garage__add,.cpg-garage__back{display:inline-block;border:1px dashed rgba(255,255,255,.35);background:transparent;color:inherit;font:inherit;font-weight:600;padding:11px 14px;border-radius:10px;cursor:pointer;width:100%}' +
      '.cpg-garage__back{border-style:solid;width:auto;margin-bottom:12px;opacity:.8;font-weight:500}' +
      /* header chip vehicle-count badge — black circle pinned to the chip corner
         (owner-specified, mirrors the cart badge) */
      '[data-cpg-garage-button]{position:relative}' +
      '.cpg-garage-count{position:absolute;top:-7px;right:-7px;display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:#111;color:#fff;font-size:11px;font-weight:800;line-height:1;border:2px solid #fff;box-sizing:content-box}' +
      /* the theme ships its own garage count badge that reads a different source;
         our cpg-garage-count is authoritative, so suppress the theme one. */
      '#cpGarageTriggerDesktop .cp-ref-garage-count,#cpGarageTrigger .cp-ref-garage-count{display:none!important}' +
      /* vehicle in garage → chip text goes green (empty garage stays theme white) */
      '[data-cpg-garage-button].cpg-garage--active .cp-ref-action__small,[data-cpg-garage-button].cpg-garage--active .cp-ref-action__strong{color:#fff!important;-webkit-text-fill-color:#fff!important}' +
      /* collection fitment banner */
      /* Fitment banner - owner mockup cpg_fit_banner_livesite_fix.html (2026-07-24) */
      '.cpg-fit-banner{display:flex;align-items:center;gap:14px;margin:16px auto;padding:14px 18px;border:1px solid #C8E6D4;border-left:4px solid #1A8754;border-radius:10px;background:linear-gradient(90deg,#E8F5EE 0%,#F0FAF3 100%);color:#0F1419}' +
      '.cpg-fit-banner__icon{width:38px;height:38px;background:#1A8754;border-radius:9px;display:flex;align-items:center;justify-content:center;color:#fff;flex:none;box-shadow:0 4px 12px rgba(26,135,84,.22)}' +
      '.cpg-fit-banner__icon svg{width:20px;height:20px}' +
      '.cpg-fit-banner__info{flex:1;min-width:0}' +
      '.cpg-fit-banner__label{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#1A8754;font-weight:800;line-height:1;margin-bottom:4px}' +
      '.cpg-fit-banner__veh{font-size:15px;color:#0F1419;font-weight:700;line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
      '.cpg-fit-banner__actions{display:flex;align-items:center;gap:8px;flex:none}' +
      '.cpg-fit-banner__change{background:#0F1419;color:#fff;border:0;border-radius:8px;padding:9px 14px;font-weight:700;font-size:13px;cursor:pointer;display:flex;align-items:center;gap:6px;text-decoration:none}' +
      '.cpg-fit-banner__change:hover{background:#2A3138}' +
      '.cpg-fit-banner__change svg{width:14px;height:14px}' +
      '.cpg-fit-banner__clear{background:transparent;color:#5A6470;border:1.5px solid #E8EAED;border-radius:8px;padding:9px 12px;font-weight:600;font-size:13px;cursor:pointer;display:flex;align-items:center;gap:6px;white-space:nowrap;text-decoration:none}' +
      '.cpg-fit-banner__clear:hover{border-color:#e21b1b;color:#e21b1b}' +
      '.cpg-fit-banner__clear svg{width:12px;height:12px}' +
      '@media(max-width:749px){.cpg-fit-banner{gap:10px;padding:10px 12px}.cpg-fit-banner__icon{width:30px;height:30px;border-radius:7px}.cpg-fit-banner__icon svg{width:16px;height:16px}.cpg-fit-banner__label{font-size:9px}.cpg-fit-banner__veh{font-size:12.5px}.cpg-fit-banner__change{padding:9px 10px}.cpg-fit-banner__change-txt,.cpg-fit-banner__clear-txt{display:none}}' +
      /* header garage chip: size to content, ellipsis long vehicle names, never
         collide with the cart. The theme shipped a fixed 142px width that clipped
         "2018 GMC Sierra 1500". */
      '#cpGarageTriggerDesktop{overflow:visible!important}' +
      '#cpGarageTriggerDesktop .cp-ref-action__text{flex:1 1 auto!important;width:auto!important;min-width:0!important;overflow:hidden!important;white-space:normal!important}' +
      '#cpGarageTriggerDesktop .cp-ref-action__strong{display:-webkit-box!important;width:100%!important;min-width:0!important;max-width:100%!important;color:#fff!important;-webkit-text-fill-color:#fff!important;white-space:normal!important;overflow:hidden!important;text-overflow:ellipsis!important;overflow-wrap:anywhere!important;word-break:break-word!important;-webkit-box-orient:vertical!important;-webkit-line-clamp:2!important;line-height:1.12!important;max-height:2.24em!important}' +
      '#cpGarageTriggerDesktop .cpg-garage-count{top:-7px!important;right:-7px!important}' +
      '.cp-ref-action--cart{flex:0 0 auto}' +
      '#cpGarageTriggerDesktop.cpg-garage--empty{cursor:pointer}' +
      /* let the search field yield space to the garage chip, not the reverse */
      '.cp-header__desktop-actions form,.cp-header__desktop-actions .cp-header__search{min-width:0}' +
      /* ── Header polish (teardown Option A) ────────────────────────────────
         The theme capped all three desktop header rows at 1200px centered
         (via #header.cp-header … rules with a --cp-*-container variable),
         which left big empty gutters on wide screens and squeezed the middle.
         Widen them to the page's own min(94%, 1440px) so the header lines up
         with the product grid below — and enlarge the small garage/cart chips.
         Selectors MUST carry #header.cp-header (repeated .cp-header to exceed
         the theme's own !important rules) or the theme out-specifies us. */
      '#header.cp-header{--cp-final-container:1440px!important;--cp-exact-container:1440px!important}' +
      '#header.cp-header.cp-header .cp-header__top-inner.cp-header__top-inner--desktop,' +
      '#header.cp-header.cp-header .cp-header__desktop-main.cp-header__desktop-main--reference,' +
      '#header.cp-header.cp-header .cp-header__desktop-nav{width:min(94%,1440px)!important;max-width:none!important;margin-left:auto!important;margin-right:auto!important}' +
      /* bigger, clearer garage + cart action chips */
      '#header.cp-header .cp-header__desktop-actions .cp-ref-action{height:54px!important;padding:0 15px!important;gap:11px!important;border-radius:10px!important}' +
      /* min-width beats the theme\'s max-width cap that otherwise clamps the icon back to 22px */
      '#header.cp-header .cp-ref-action__garage-visual svg{width:27px!important;height:27px!important;min-width:27px!important;min-height:27px!important;max-width:none!important;max-height:none!important}' +
      '#header.cp-header .cp-header__desktop-actions .cp-ref-action__small{font-size:11px!important}' +
      '#header.cp-header .cp-header__desktop-actions .cp-ref-action__strong{font-size:14.5px!important}' +
      /* garage chip: drop the fixed 142px cap so the vehicle name isn\'t clipped */
      '#header.cp-header #cpGarageTriggerDesktop{width:auto!important;min-width:150px!important;max-width:250px!important}' +
      /* ── mobile stepped vehicle picker (owner mockup mobile-picker v1) ──────
         Full-screen sheet that replaces the stacked native <select> cascade on
         phones. Opens from the header car icon (data-cpg-garage-button) via
         openPickerModal(). Reuses the same fitment cascade API. */
      '.cpg-ms{position:fixed;inset:0;z-index:100000;background:#fff;color:#15181D;display:flex;flex-direction:column;transform:translateY(100%);transition:transform .24s cubic-bezier(.2,.8,.3,1);font-family:inherit}' +
      '.cpg-ms.is-on{transform:translateY(0)}' +
      '@media(prefers-reduced-motion:reduce){.cpg-ms{transition:none}}' +
      '.cpg-ms__top{background:#E31E24;color:#fff;padding:12px;display:flex;align-items:center;gap:10px;flex:0 0 auto}' +
      '.cpg-ms__back{width:44px;height:44px;border-radius:8px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.32);color:#fff;font-size:18px;cursor:pointer;font-family:inherit;flex:0 0 auto}' +
      '.cpg-ms__back[disabled]{opacity:.35;cursor:default}' +
      '.cpg-ms__step{font-size:10.5px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;opacity:.92}' +
      '.cpg-ms__title{font-size:16.5px;font-weight:800;letter-spacing:-.01em}' +
      '.cpg-ms__x{margin-left:auto;width:44px;height:44px;border:0;background:transparent;color:#fff;font-size:20px;cursor:pointer;font-family:inherit;flex:0 0 auto}' +
      '.cpg-ms__search{position:relative;padding:11px 12px;border-bottom:1px solid #E5E7EB;flex:0 0 auto}' +
      '.cpg-ms__search::before{content:"\\2315";position:absolute;left:24px;top:22px;color:#9CA3AF;font-size:17px;line-height:1;pointer-events:none}' +
      '.cpg-ms__search input{width:100%;height:46px;border:1px solid #E5E7EB;border-radius:8px;padding:0 14px 0 38px;font-size:16px;font-family:inherit;color:#15181D;background:#F4F5F7}' +
      '.cpg-ms__search input:focus{outline:2px solid #E31E24;outline-offset:1px;background:#fff}' +
      '.cpg-ms__list{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch}' +
      '.cpg-ms__opt{min-height:56px;width:100%;display:flex;align-items:center;gap:12px;padding:0 14px;border:0;border-bottom:1px solid #F1F2F4;background:#fff;cursor:pointer;font-size:15px;font-family:inherit;color:#15181D;text-align:left;font-weight:500}' +
      '.cpg-ms__opt:active{background:#F1F2F4}' +
      '.cpg-ms__opt .cpg-ms__chev{margin-left:auto;color:#9CA3AF;font-size:16px}' +
      '.cpg-ms__opt.is-pop{background:#FDECED}' +
      '.cpg-ms__opt .cpg-ms__pin{font-size:9.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#E31E24;border:1px solid currentColor;border-radius:999px;padding:2px 7px}' +
      '.cpg-ms__lbl{padding:10px 14px 7px;font-size:10px;font-weight:800;letter-spacing:.13em;text-transform:uppercase;color:#9CA3AF;background:#F4F5F7;border-bottom:1px solid #F1F2F4}' +
      '.cpg-ms__skip{padding:14px;border-top:1px solid #F1F2F4}' +
      '.cpg-ms__skipbtn{width:100%;min-height:44px;border:1px solid #E5E7EB;background:#fff;border-radius:8px;font-size:14px;font-weight:700;font-family:inherit;color:#374151;cursor:pointer}' +
      '.cpg-ms__hint{font-size:12px;color:#6B7280;margin:8px 2px 0;text-align:center}' +
      '.cpg-ms__empty{padding:34px 20px;text-align:center;color:#6B7280;font-size:14px}' +
      '.cpg-ms__loading{padding:34px 20px;text-align:center;color:#9CA3AF;font-size:14px}' +
      '.cpg-ms__crumb{flex:0 0 auto;display:flex;align-items:center;gap:5px;padding:9px 12px;border-top:1px solid #E5E7EB;background:#F4F5F7;min-height:52px;flex-wrap:wrap}' +
      '.cpg-ms__crumb button{border:1px solid #E5E7EB;background:#fff;font-family:inherit;font-size:12.5px;font-weight:800;color:#15181D;cursor:pointer;padding:6px 10px;border-radius:6px;min-height:32px}' +
      '.cpg-ms__crumb button.is-todo{color:#9CA3AF;border-style:dashed;background:transparent;cursor:default;font-weight:600}' +
      '.cpg-ms__crumb .cpg-ms__sep{color:#9CA3AF;font-size:11px}' +
      '.cpg-ms__done{position:absolute;inset:0;background:rgba(21,24,29,.5);display:flex;align-items:flex-end;z-index:30;opacity:0;pointer-events:none;transition:opacity .18s}' +
      '.cpg-ms__done.is-on{opacity:1;pointer-events:auto}' +
      '.cpg-ms__donecard{background:#fff;width:100%;border-radius:16px 16px 0 0;padding:24px 18px 18px;text-align:center}' +
      '.cpg-ms__tick{width:48px;height:48px;border-radius:50%;background:#E8F6EE;color:#0B7A3D;display:flex;align-items:center;justify-content:center;font-size:23px;margin:0 auto 11px;font-weight:800}' +
      '.cpg-ms__donev{margin:0 0 4px;font-size:17px;font-weight:800}' +
      '.cpg-ms__donen{margin:0 0 15px;font-size:13.5px;color:#6B7280}' +
      '.cpg-ms__pri{width:100%;min-height:48px;background:#E31E24;color:#fff;border:0;border-radius:8px;font-size:14.5px;font-weight:800;font-family:inherit;cursor:pointer;text-transform:uppercase;letter-spacing:.04em}' +
      '.cpg-ms__ghost{width:100%;min-height:44px;background:transparent;color:#6B7280;border:0;font-size:13.5px;font-weight:700;font-family:inherit;cursor:pointer;margin-top:6px}' +
      '';
    document.head.appendChild(style);
  }

  // ── scoped-finder support (PDP) ────────────────────────────────────────────
  // A product's fitment_vehicles metafield ("year|make|model|submodel|engine" lines,
  // year may be a range) is emitted as JSON on the PDP. parseScopedApps expands it into
  // a flat {year,make,model,submodel} list so the cascade can offer ONLY the vehicles
  // this part actually fits — no global picker, no accidental "does not fit".
  function parseScopedApps(raw) {
    var text = raw;
    try { var j = JSON.parse(raw); if (typeof j === 'string') text = j; } catch (e) {}
    var out = [];
    String(text || '').split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line || line.indexOf('|') === -1) return;
      var c = line.split('|');
      var make = (c[1] || '').trim();
      var model = (c[2] || '').trim();
      var sub = (c[3] || '').trim();
      if (!make || !model) return;
      var yr = (c[0] || '').trim(), from, to;
      if (yr.indexOf('-') !== -1) { var p = yr.split('-'); from = parseInt(p[0], 10); to = parseInt(p[1], 10); }
      else { from = to = parseInt(yr, 10); }
      if (!from || !to || to < from || (to - from) > 60) return;
      for (var y = from; y <= to; y += 1) out.push({ year: y, make: make, model: model, submodel: sub });
    });
    return out.length ? out : null;
  }
  function scopedUniq(arr) {
    var seen = {}, o = [];
    arr.forEach(function (v) { if (v != null && v !== '' && !seen[v]) { seen[v] = 1; o.push(v); } });
    return o;
  }

  // ── one cascade instance ───────────────────────────────────────────────────
  function initHost(host) {
    if (host.__cpgInit) return;
    host.__cpgInit = true;
    var base = host.getAttribute('data-cpg-api-base') || apiBase();
    var onGo = host.getAttribute('data-cpg-on-go') || 'save'; // save | confirm

    // PDP scoped finder: constrain the cascade to THIS product's fitment applications
    // (data-cpg-scope-from → a JSON blob of the fitment_vehicles metafield). When set,
    // the Year/Make/Model/Submodel options come from the part's own fitting vehicles —
    // so every complete selection is guaranteed to fit. null → the normal global picker.
    var scopedApps = null;
    var scopeSel = host.getAttribute('data-cpg-scope-from');
    if (scopeSel) {
      var scopeNode = document.querySelector(scopeSel);
      if (scopeNode) { try { scopedApps = parseScopedApps(scopeNode.textContent); } catch (e) { scopedApps = null; } }
    }
    function scYears() { return scopedUniq(scopedApps.map(function (a) { return a.year; })).sort(function (a, b) { return b - a; }).map(String); }
    function scMakes(y) { return scopedUniq(scopedApps.filter(function (a) { return String(a.year) === String(y); }).map(function (a) { return a.make; })).sort(); }
    function scModels(y, mk) { return scopedUniq(scopedApps.filter(function (a) { return String(a.year) === String(y) && a.make === mk; }).map(function (a) { return a.model; })).sort(); }
    function scSubs(y, mk, md) { return scopedUniq(scopedApps.filter(function (a) { return String(a.year) === String(y) && a.make === mk && a.model === md && a.submodel; }).map(function (a) { return a.submodel; })).sort(); }
    // Deep-link prefill (makes-index page): once the shopper picks a year, the
    // cascade auto-selects this make (and model) if the year actually has them.
    var pendingMake = host.getAttribute('data-cpg-prefill-make') || null;
    var pendingModel = host.getAttribute('data-cpg-prefill-model') || null;
    // Per-instance CTA label (owner: homepage hero says "Find my parts"); others default "GO".
    var goLabel = host.getAttribute('data-cpg-ymm-go-label') || 'GO';
    // Labeled mode (homepage hero, screen 07): a field label above each select, "Select"/"Any"
    // placeholders, and a CTA arrow. Off by default so the compact pickers stay unchanged.
    var labeled = host.hasAttribute('data-cpg-ymm-labels');
    // Mockup 07c (mobile hero) drops the field labels and puts the field NAME inside
    // the select. Labels are hidden by CSS at <=749px, so the placeholder has to carry
    // the name there or the boxes read as four anonymous "Select" dropdowns.
    // Desktop keeps the label-above + "Select"/"Any" pattern unchanged.
    var narrow = typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 749px)').matches;
    var PH = (labeled && !narrow)
      ? { year: 'Select', make: 'Select', model: 'Select', submodel: 'Any' }
      : { year: 'Year', make: 'Make', model: 'Model', submodel: 'Submodel (optional)' };

    // Engine is deliberately NOT part of the browse picker (owner rule: 4 fields
    // max; engine is asked contextually on the PDP only when a product's fitment
    // actually splits by engine).
    // a11y (PSI 2026-07-23): each select needs an accessible name for screen readers / agents.
    // In labeled mode each select is wrapped with a visible field label (screen 07).
    function fieldHtml(key, label, ph, disabled) {
      var selHtml = '<select data-cpg-sel="' + key + '" aria-label="Vehicle ' + key + '"' +
        (disabled ? ' disabled' : '') + '><option value="">' + ph + '</option></select>';
      return labeled
        ? '<div class="cpg-ymm__field"><span class="cpg-ymm__flabel">' + label + '</span>' + selHtml + '</div>'
        : selHtml;
    }
    host.innerHTML =
      '<div class="cpg-ymm' + (labeled ? ' cpg-ymm--labeled' : '') + '">' +
      fieldHtml('year', 'Year', PH.year, false) +
      fieldHtml('make', 'Make', PH.make, true) +
      fieldHtml('model', 'Model', PH.model, true) +
      fieldHtml('submodel', 'Submodel (optional)', PH.submodel, true) +
      '<div class="cpg-ymm__actions">' +
      '<button type="button" class="cpg-ymm__go" data-cpg-go disabled>' + goLabel +
      (labeled ? ' <span class="cpg-ymm__go-arrow" aria-hidden="true">&#8594;</span>' : '') + '</button>' +
      '<button type="button" class="cpg-ymm__reset" data-cpg-reset title="Reset">&#8635;</button>' +
      '</div>' +
      '<p class="cpg-ymm__confirm" data-cpg-confirm></p>' +
      '</div>';

    var sel = {};
    ['year', 'make', 'model', 'submodel'].forEach(function (k) {
      sel[k] = host.querySelector('[data-cpg-sel="' + k + '"]');
    });
    var goBtn = host.querySelector('[data-cpg-go]');
    var resetBtn = host.querySelector('[data-cpg-reset]');
    var confirmEl = host.querySelector('[data-cpg-confirm]');
    var wrap = host.querySelector('.cpg-ymm');

    function fill(select, values, placeholder, keep) {
      select.innerHTML = '<option value="">' + placeholder + '</option>';
      (values || []).forEach(function (v) {
        var o = document.createElement('option');
        o.value = v; o.textContent = v;
        select.appendChild(o);
      });
      select.disabled = !values || values.length === 0;
      if (keep && values && values.indexOf(keep) !== -1) select.value = keep;
    }

    function api(path) {
      return fetch(base + path).then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.json();
      });
    }

    function updateGo() {
      goBtn.disabled = !(sel.year.value && sel.make.value && sel.model.value);
    }

    function resetBelow(level) {
      var order = ['make', 'model', 'submodel'];
      for (var i = order.indexOf(level); i < order.length; i += 1) {
        var k = order[i];
        fill(sel[k], [], PH[k]);
      }
      updateGo();
    }

    function loadMakes(keep) {
      if (scopedApps) { fill(sel.make, scMakes(sel.year.value), PH.make, keep); return Promise.resolve(); }
      return api('/makes?year=' + sel.year.value).then(function (d) { fill(sel.make, d.makes, PH.make, keep); });
    }
    function loadModels(keep) {
      if (scopedApps) { fill(sel.model, scModels(sel.year.value, sel.make.value), PH.model, keep); return Promise.resolve(); }
      return api('/models?year=' + sel.year.value + '&make=' + encodeURIComponent(sel.make.value)).then(function (d) { fill(sel.model, d.models, PH.model, keep); });
    }
    function loadSubs(keepSub) {
      if (scopedApps) { fill(sel.submodel, scSubs(sel.year.value, sel.make.value, sel.model.value), PH.submodel, keepSub); return Promise.resolve(); }
      var q = '?year=' + sel.year.value + '&make=' + encodeURIComponent(sel.make.value) + '&model=' + encodeURIComponent(sel.model.value);
      return api('/submodels' + q).then(function (d) { fill(sel.submodel, d.submodels, PH.submodel, keepSub); });
    }

    sel.year.addEventListener('change', function () {
      resetBelow('make');
      if (!sel.year.value) return;
      loadMakes(pendingMake).then(function () {
        if (!pendingMake || sel.make.value !== pendingMake) return;
        return loadModels(pendingModel).then(function () {
          if (pendingModel && sel.model.value === pendingModel) return loadSubs().then(updateGo);
          updateGo();
        });
      }).catch(function () {});
    });
    sel.make.addEventListener('change', function () {
      resetBelow('model');
      if (sel.make.value) loadModels().catch(function () {});
    });
    sel.model.addEventListener('change', function () {
      resetBelow('submodel');
      if (sel.model.value) loadSubs().catch(function () {});
      updateGo();
    });
    sel.submodel.addEventListener('change', updateGo);

    goBtn.addEventListener('click', function () {
      if (goBtn.disabled) return;
      var v = {
        year: parseInt(sel.year.value, 10),
        make: sel.make.value,
        model: sel.model.value,
        submodel: sel.submodel.value || null,
        engine: null
      };
      v.label = vehicleLabel(v);
      host.__cpgSelfWrite = true;
      writeGarage(v);
      host.__cpgSelfWrite = false;
      if (onGo === 'confirm') {
        // Hero GO = save AND take the shopper to Shop All scoped to the vehicle.
        confirmEl.textContent = '✓ Vehicle saved — loading every part that fits…';
        wrap.classList.add('cpg-ymm--saved');
        setTimeout(function () { location.href = vehicleResultsUrl(v); }, 350);
      }
      if (host.__cpgOnSaved) host.__cpgOnSaved();
    });

    resetBtn.addEventListener('click', function () {
      host.__cpgSelfWrite = true;
      window.CPGFitment.clearVehicle(); // Option A: remove, not just deactivate
      host.__cpgSelfWrite = false;
      sel.year.value = '';
      resetBelow('make');
      wrap.classList.remove('cpg-ymm--saved');
    });

    function prefill() {
      // "Add a vehicle" starts blank so a second vehicle isn't pre-populated with
      // the active one; everywhere else the cascade prefills the active vehicle.
      var g = host.hasAttribute('data-cpg-blank') ? null : readGarage();
      var yearsPromise = scopedApps ? Promise.resolve({ years: scYears() }) : api('/years');
      return yearsPromise.then(function (d) {
        fill(sel.year, (d.years || []).map(String), PH.year, g ? String(g.year) : null);
        if (!g || sel.year.value !== String(g.year)) return;
        return loadMakes(g.make).then(function () {
          if (sel.make.value !== g.make) return;
          return loadModels(g.model).then(function () {
            if (sel.model.value !== g.model) return;
            return loadSubs(g.submodel).then(updateGo);
          });
        });
      }).catch(function () { /* API unreachable — selects stay empty */ });
    }

    // keep this instance in sync when ANOTHER instance changes the garage
    window.addEventListener('cpg:vehicle-changed', function () {
      if (host.__cpgSelfWrite) return;
      prefill();
    });

    prefill();
  }

  // ── garage modal (header garage button) — saved vehicles + add ─────────────
  var modalEl = null;
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  /** Attr-safe (single/double quotes → entities) for values dropped into data-*. */
  function escAttr(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  // ── mobile stepped vehicle picker (owner mockup mobile-picker v1) ──────────
  // Popular makes pinned above the full make list. Owner note: drive from real
  // order volume later; this is a sensible default for a truck-heavy catalogue.
  var POPULAR_MAKES = ['Ford', 'Chevrolet', 'Ram', 'Toyota', 'GMC', 'Jeep'];
  function isMobileViewport() {
    try { return window.matchMedia('(max-width:749px)').matches; } catch (e) { return (window.innerWidth || 999) <= 749; }
  }

  var msEl = null;
  /** Full-screen stepped Year→Make→Model→Submodel sheet. Reuses the fitment
   *  cascade API; on finish saves to the garage and offers "shop parts that fit"
   *  with the REAL fitting-part count (never a fabricated number). */
  function openMobileStepSheet() {
    injectCss();
    if (msEl) { msEl.remove(); msEl = null; }
    var base = apiBase();
    var S = { step: 1, y: null, mk: null, md: null, sm: null };
    var cache = { years: null, makes: {}, models: {}, subs: {} };
    var opts = [];
    var loadToken = 0;
    var TITLES = ['', 'Select a year', 'Select a make', 'Select a model', 'Select a submodel'];

    msEl = document.createElement('div');
    msEl.className = 'cpg-ms';
    msEl.setAttribute('role', 'dialog');
    msEl.setAttribute('aria-modal', 'true');
    msEl.innerHTML =
      '<div class="cpg-ms__top">' +
        '<button type="button" class="cpg-ms__back" data-ms-back aria-label="Back">&#8592;</button>' +
        '<div><div class="cpg-ms__step" data-ms-step></div><div class="cpg-ms__title" data-ms-title></div></div>' +
        '<button type="button" class="cpg-ms__x" data-ms-close aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="cpg-ms__search" data-ms-searchwrap style="display:none">' +
        '<input type="text" data-ms-search autocomplete="off" aria-label="Search options">' +
      '</div>' +
      '<div class="cpg-ms__list" data-ms-list></div>' +
      '<div class="cpg-ms__crumb" data-ms-crumb></div>' +
      '<div class="cpg-ms__done" data-ms-done>' +
        '<div class="cpg-ms__donecard">' +
          '<div class="cpg-ms__tick">&#10003;</div>' +
          '<div class="cpg-ms__donev" data-ms-donev></div>' +
          '<div class="cpg-ms__donen" data-ms-donen>Vehicle saved</div>' +
          '<button type="button" class="cpg-ms__pri" data-ms-shop>Shop parts that fit</button>' +
          '<button type="button" class="cpg-ms__ghost" data-ms-keep>Keep browsing</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(msEl);
    try { document.documentElement.style.overflow = 'hidden'; } catch (e) {}

    var listEl = msEl.querySelector('[data-ms-list]');
    var searchWrap = msEl.querySelector('[data-ms-searchwrap]');
    var searchInput = msEl.querySelector('[data-ms-search]');
    var stepEl = msEl.querySelector('[data-ms-step]');
    var titleEl = msEl.querySelector('[data-ms-title]');
    var backBtn = msEl.querySelector('[data-ms-back]');
    var crumbEl = msEl.querySelector('[data-ms-crumb]');
    var doneEl = msEl.querySelector('[data-ms-done]');

    function api(path) { return fetch(base + path).then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); }); }

    function onKey(e) { if (e.key === 'Escape') closeMs(); }
    function closeMs() {
      document.removeEventListener('keydown', onKey);
      try { document.documentElement.style.overflow = ''; } catch (e) {}
      var el = msEl; if (!el) return;
      el.classList.remove('is-on');
      setTimeout(function () { if (el) el.remove(); if (msEl === el) msEl = null; }, 200);
    }
    document.addEventListener('keydown', onKey);

    function built() {
      var v = { year: parseInt(S.y, 10), make: S.mk, model: S.md, submodel: S.sm || null, engine: null };
      v.label = vehicleLabel(v); return v;
    }

    function currentOptions() {
      if (S.step === 1) {
        if (cache.years) return Promise.resolve(cache.years);
        return api('/years').then(function (d) { return (cache.years = (d.years || []).map(String)); });
      }
      if (S.step === 2) {
        if (cache.makes[S.y]) return Promise.resolve(cache.makes[S.y]);
        return api('/makes?year=' + encodeURIComponent(S.y)).then(function (d) { return (cache.makes[S.y] = d.makes || []); });
      }
      if (S.step === 3) {
        var km = S.y + '|' + S.mk;
        if (cache.models[km]) return Promise.resolve(cache.models[km]);
        return api('/models?year=' + encodeURIComponent(S.y) + '&make=' + encodeURIComponent(S.mk)).then(function (d) { return (cache.models[km] = d.models || []); });
      }
      var ks = S.y + '|' + S.mk + '|' + S.md;
      if (cache.subs[ks]) return Promise.resolve(cache.subs[ks]);
      return api('/submodels?year=' + encodeURIComponent(S.y) + '&make=' + encodeURIComponent(S.mk) + '&model=' + encodeURIComponent(S.md)).then(function (d) { return (cache.subs[ks] = d.submodels || []); });
    }

    function renderStep() {
      stepEl.textContent = 'Step ' + S.step + ' of 4';
      titleEl.textContent = TITLES[S.step];
      backBtn.disabled = S.step === 1;
      var wantSearch = S.step === 2 || S.step === 3;
      searchWrap.style.display = wantSearch ? 'block' : 'none';
      searchInput.value = '';
      searchInput.placeholder = S.step === 2 ? 'Search makes' : 'Search models';
      renderCrumb();
      listEl.innerHTML = '<div class="cpg-ms__loading">Loading…</div>';
      var my = ++loadToken;
      currentOptions().then(function (arr) {
        if (my !== loadToken) return;
        opts = arr || [];
        // Submodel step with no data → nothing to ask; finish on all-submodels.
        if (S.step === 4 && !opts.length) { S.sm = null; return finish(); }
        renderList();
        if (wantSearch) setTimeout(function () { try { searchInput.focus(); } catch (e) {} }, 260);
      }).catch(function () {
        if (my !== loadToken) return;
        listEl.innerHTML = '<div class="cpg-ms__empty">Couldn’t load options — check your connection and try again.</div>';
      });
    }

    function renderList() {
      var q = (searchInput.value || '').trim().toLowerCase();
      var all = opts || [];
      var hits = q ? all.filter(function (o) { return String(o).toLowerCase().indexOf(q) > -1; }) : all;
      var h = '';
      if (S.step === 2 && !q) {
        var pops = POPULAR_MAKES.filter(function (m) { return all.indexOf(m) > -1; });
        if (pops.length) {
          h += '<div class="cpg-ms__lbl">Popular</div>';
          pops.forEach(function (m) { h += '<button type="button" class="cpg-ms__opt is-pop" data-ms-opt data-ms-val="' + escAttr(m) + '">' + esc(m) + '<span class="cpg-ms__pin">Popular</span><span class="cpg-ms__chev">›</span></button>'; });
          h += '<div class="cpg-ms__lbl">All makes</div>';
        }
      }
      if (!hits.length) {
        h += '<div class="cpg-ms__empty">No matches' + (q ? ' for “' + esc(q) + '”' : '') + '.</div>';
      } else {
        hits.forEach(function (o) { h += '<button type="button" class="cpg-ms__opt" data-ms-opt data-ms-val="' + escAttr(o) + '">' + esc(o) + '<span class="cpg-ms__chev">›</span></button>'; });
      }
      if (S.step === 4) {
        h += '<div class="cpg-ms__skip"><button type="button" class="cpg-ms__skipbtn" data-ms-skip>Skip — show parts for all submodels</button><p class="cpg-ms__hint">Most parts don’t need your submodel. You can add it later.</p></div>';
      }
      listEl.innerHTML = h;
      listEl.scrollTop = 0;
    }

    function renderCrumb() {
      var parts = [{ v: S.y, s: 1, ph: 'Year' }, { v: S.mk, s: 2, ph: 'Make' }, { v: S.md, s: 3, ph: 'Model' }, { v: S.sm, s: 4, ph: 'Submodel' }];
      crumbEl.innerHTML = parts.map(function (p, i) {
        var sep = i ? '<span class="cpg-ms__sep">›</span>' : '';
        return p.v ? sep + '<button type="button" data-ms-jump="' + p.s + '">' + esc(p.v) + '</button>'
                   : sep + '<button type="button" class="is-todo" disabled>' + p.ph + '</button>';
      }).join('');
    }

    function pick(val) {
      if (S.step === 1) { S.y = val; S.step = 2; }
      else if (S.step === 2) { S.mk = val; S.step = 3; }
      else if (S.step === 3) { S.md = val; S.step = 4; }
      else { S.sm = val || null; return finish(); }
      renderStep();
    }
    function gotoStep(n) {
      if (n === 1) { S.y = S.mk = S.md = S.sm = null; }
      else if (n === 2) { S.mk = S.md = S.sm = null; }
      else if (n === 3) { S.md = S.sm = null; }
      else if (n === 4) { S.sm = null; }
      S.step = n; renderStep();
    }
    function stepBack() { if (S.step > 1) gotoStep(S.step - 1); }

    function finish() {
      var v = built();
      addToGarage(v);
      msEl.querySelector('[data-ms-donev]').textContent = v.label;
      doneEl.classList.add('is-on');
      // Honest count: fetch the REAL number of parts that fit; show it when it
      // lands, leaving "Vehicle saved" until then. Never a fabricated figure.
      api('/products?year=' + encodeURIComponent(v.year) + '&make=' + encodeURIComponent(v.make) + '&model=' + encodeURIComponent(v.model))
        .then(function (d) {
          if (!msEl) return;
          var n = d && typeof d.total === 'number' ? d.total : null;
          var el = msEl.querySelector('[data-ms-donen]');
          if (n != null && el) el.textContent = n.toLocaleString() + ' part' + (n === 1 ? '' : 's') + ' fit this vehicle';
        }).catch(function () {});
    }

    msEl.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-ms-close]') || t.closest('[data-ms-keep]')) return closeMs();
      if (t.closest('[data-ms-shop]')) { location.href = vehicleResultsUrl(built()); return; }
      if (t.closest('[data-ms-back]')) return stepBack();
      var opt = t.closest('[data-ms-opt]'); if (opt) return pick(opt.getAttribute('data-ms-val'));
      if (t.closest('[data-ms-skip]')) return pick('');
      var jmp = t.closest('[data-ms-jump]'); if (jmp) return gotoStep(parseInt(jmp.getAttribute('data-ms-jump'), 10));
    });
    searchInput.addEventListener('input', renderList);

    // slide in on the next frame so the transform transition plays; setTimeout
    // fallback guarantees the reveal even where rAF is throttled (hidden tab).
    window.requestAnimationFrame(function () { if (msEl) msEl.classList.add('is-on'); });
    setTimeout(function () { if (msEl) msEl.classList.add('is-on'); }, 60);
    renderStep();
  }

  // MOCKUP 2026-10-02 (owner): adding a vehicle always uses the Year > Make > Model > Trim > Engine
  // stepper (CPGFitFlow.openStepper, cpg-fit-flow.js). The old dropdown cascade + mobile sheet had
  // bugs. The saved-vehicles list below (switch / remove) stays as it was.
  function hasStepper() { return !!(window.CPGFitFlow && typeof window.CPGFitFlow.openStepper === 'function'); }
  function openPickerModal() {
    injectCss();
    if (hasStepper() && !readGarageList().length) { closePicker(); return window.CPGFitFlow.openStepper(); }
    // Mobile with an empty garage → go straight to the stepped sheet (the
    // documented fix for stacked native selects). With saved vehicles we still
    // show the list modal so the shopper can switch; its "Add a vehicle" routes
    // to the sheet (see showAddView).
    if (isMobileViewport() && !readGarageList().length) { return openMobileStepSheet(); }
    if (modalEl) { modalEl.remove(); modalEl = null; }
    modalEl = document.createElement('div');
    modalEl.className = 'cpg-ymm-modal';
    modalEl.innerHTML =
      '<div class="cpg-ymm-modal__backdrop" data-cpg-modal-close></div>' +
      '<div class="cpg-ymm-modal__card">' +
      '<button type="button" class="cpg-ymm-modal__close" data-cpg-modal-close aria-label="Close">&times;</button>' +
      '<h3 class="cpg-ymm-modal__title">My Garage</h3>' +
      '<p class="cpg-ymm-modal__sub">Save vehicles and we’ll check fitment on every product and filter each collection to fit.</p>' +
      '<div class="cpg-garage" data-cpg-garage></div>' +
      '</div>';
    document.body.appendChild(modalEl);
    renderGarage();
    modalEl.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.hasAttribute && t.hasAttribute('data-cpg-modal-close')) return closePicker();
      var selBtn = t.closest && t.closest('[data-cpg-pick]');
      if (selBtn) { var v = readGarageList().filter(function (x) { return vehicleId(x) === selBtn.getAttribute('data-cpg-pick'); })[0]; if (v) { setActiveVehicle(v); closePicker(); } return; }
      var rmBtn = t.closest && t.closest('[data-cpg-remove]');
      if (rmBtn) { removeFromGarage(rmBtn.getAttribute('data-cpg-remove')); renderGarage(); return; }
      var addBtn = t.closest && t.closest('[data-cpg-add-toggle]');
      if (addBtn) { showAddView(); return; }
    });
    document.addEventListener('keydown', escClose);
  }

  function renderGarage() {
    var wrap = modalEl && modalEl.querySelector('[data-cpg-garage]');
    if (!wrap) return;
    var list = readGarageList();
    var active = readGarage();
    var activeId = active ? vehicleId(active) : null;
    if (!list.length) { showAddView(); return; }
    var rows = list.map(function (v) {
      var id = vehicleId(v);
      var isActive = id === activeId;
      return '<div class="cpg-garage-item' + (isActive ? ' is-active' : '') + '">' +
        '<button type="button" class="cpg-garage-item__pick" data-cpg-pick="' + esc(id) + '">' +
        '<span class="cpg-garage-item__dot" aria-hidden="true"></span>' +
        '<span class="cpg-garage-item__label">' + esc(v.label || vehicleLabel(v)) + '</span>' +
        (isActive ? '<span class="cpg-garage-item__tag">Active</span>' : '') +
        '</button>' +
        '<button type="button" class="cpg-garage-item__remove" data-cpg-remove="' + esc(id) + '" aria-label="Remove">&times;</button>' +
        '</div>';
    }).join('');
    wrap.innerHTML =
      '<div class="cpg-garage-list">' + rows + '</div>' +
      '<button type="button" class="cpg-garage__add" data-cpg-add-toggle>+ Add a vehicle</button>';
  }

  function showAddView() {
    if (hasStepper()) { closePicker(); return window.CPGFitFlow.openStepper(); }
    // On mobile, "Add a vehicle" opens the stepped sheet instead of the inline
    // stacked selects — close the list modal first so they don't stack.
    if (isMobileViewport()) { closePicker(); return openMobileStepSheet(); }
    var wrap = modalEl && modalEl.querySelector('[data-cpg-garage]');
    if (!wrap) return;
    var hasSaved = readGarageList().length > 0;
    wrap.innerHTML =
      (hasSaved ? '<button type="button" class="cpg-garage__back" data-cpg-garage-back>&#8592; Back to garage</button>' : '') +
      '<div data-cpg-ymm-widget data-cpg-on-go="save" data-cpg-blank></div>';
    var host = wrap.querySelector('[data-cpg-ymm-widget]');
    host.__cpgOnSaved = function () { closePicker(); };
    initHost(host);
    var back = wrap.querySelector('[data-cpg-garage-back]');
    if (back) back.addEventListener('click', renderGarage);
  }

  function escClose(e) { if (e.key === 'Escape') closePicker(); }
  function closePicker() {
    if (modalEl) { modalEl.remove(); modalEl = null; }
    document.removeEventListener('keydown', escClose);
  }

  // ── header garage button decoration ────────────────────────────────────────
  function decorateGarageButtons() {
    var refreshQueued = false;
    var observer = null;

    function queueRefresh() {
      if (refreshQueued) return;
      refreshQueued = true;

      window.requestAnimationFrame(function () {
        refreshQueued = false;
        refresh();
      });
    }

    function ensureCounter(btn, count) {
      var badge = btn.querySelector(':scope > .cpg-garage-count');

      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'cpg-garage-count';
        badge.setAttribute('aria-hidden', 'true');
        btn.appendChild(badge);
      }

      var nextCount = String(Math.max(0, count || 0));
      if (badge.textContent !== nextCount) badge.textContent = nextCount;
      badge.hidden = count < 1;
      return badge;
    }

    function setText(node, value) {
      if (node && node.textContent !== value) {
        node.textContent = value;
      }
    }

    function refresh() {
      var v = readGarage();
      var count = readGarageList().length;
      var label = v ? (v.label || vehicleLabel(v)) : '';
      var liveButtons = document.querySelectorAll('[data-cpg-garage-button]');

      liveButtons.forEach(function (btn) {
        var small = btn.querySelector('.cp-ref-action__small');
        var strong = btn.querySelector('.cp-ref-action__strong');

        btn.querySelectorAll('.cp-ref-garage-count').forEach(function (el) {
          el.style.setProperty('display', 'none', 'important');
        });

        btn.style.cursor = 'pointer';

        if (v) {
          btn.classList.remove('cpg-garage--empty');
          btn.classList.add('cpg-garage--active');

          setText(small, 'Shopping for');
          setText(strong, label);

          if (small) { small.style.setProperty('color', '#ffffff', 'important'); small.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important'); }
          if (strong) {
            strong.style.setProperty('color', '#ffffff', 'important');
            strong.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
            strong.setAttribute('title', label);
          }

          // The vehicle-COUNT badge belongs only on the garage ENTRY icons — never on the in-panel
          // action buttons (Add a vehicle / Manage garage / Switch / mobile vehicle card). Those are
          // also tagged [data-cpg-garage-button] for click handling, so the decorate loop was stamping
          // a stray "2" at top:-5/right:-5 over the open garage panel (owner 2026-08-22). Skip them and
          // strip any badge already stuck on one.
          if (/cpg-dock__add|cpg-dock__manage|cpg-vbar__add|cpg-vbar__switch|cpg-mnav__vehcard/.test(btn.className || '')) {
            var strayBadge = btn.querySelector(':scope > .cpg-garage-count');
            if (strayBadge) strayBadge.remove();
          } else {
            ensureCounter(btn, count);
          }
          btn.setAttribute(
            'title',
            count > 1 ? label + ' — ' + count + ' vehicles in garage' : label
          );
        } else {
          btn.classList.add('cpg-garage--empty');
          btn.classList.remove('cpg-garage--active');

          setText(small, 'Find parts for');
          setText(strong, '+ Add Vehicle');

          if (small) { small.style.removeProperty('color'); small.style.removeProperty('-webkit-text-fill-color'); }
          if (strong) {
            strong.style.removeProperty('color');
            strong.style.removeProperty('-webkit-text-fill-color');
            strong.removeAttribute('title');
          }

          var oldBadge = btn.querySelector(':scope > .cpg-garage-count');
          if (oldBadge) oldBadge.remove();

          btn.setAttribute('title', 'Add your vehicle to check fitment');
        }
      });

      bindObserver();
    }

    function bindObserver() {
      if (!window.MutationObserver) return;

      var actions = document.querySelector('.cp-header__desktop-actions');
      if (!actions) return;

      if (observer && observer.__cpgTarget === actions) return;
      if (observer) observer.disconnect();

      observer = new MutationObserver(function (mutations) {
        var shouldRefresh = mutations.some(function (mutation) {
          var target = mutation.target;
          var element = target && target.nodeType === 3 ? target.parentElement : target;

          if (!element || !element.closest) return false;
          if (element.closest('.cpg-garage-count')) return false;

          return Boolean(
            element.closest('[data-cpg-garage-button]') ||
            Array.prototype.some.call(mutation.addedNodes || [], function (node) {
              return node.nodeType === 1 && (
                node.matches && node.matches('[data-cpg-garage-button]') ||
                node.querySelector && node.querySelector('[data-cpg-garage-button]')
              );
            })
          );
        });

        if (shouldRefresh) queueRefresh();
      });

      observer.__cpgTarget = actions;
      observer.observe(actions, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    window.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest
        ? e.target.closest('[data-cpg-garage-button]')
        : null;

      if (!btn) return;
      if (e.target.closest('.cpg-ymm-modal')) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      openPickerModal();
    }, true);

    window.addEventListener('cpg:vehicle-changed', queueRefresh);
    window.addEventListener('storage', function (event) {
      if (event.key === GARAGE_KEY || event.key === GARAGE_LIST_KEY) {
        queueRefresh();
      }
    });

    document.addEventListener('shopify:section:load', queueRefresh);

    [0, 120, 350, 800, 1500, 2500].forEach(function (delay) {
      window.setTimeout(queueRefresh, delay);
    });

    refresh();
  }

  // ── collection ⇄ garage filter wiring ──────────────────────────────────────
  // The active vehicle drives collection filtering too, so a vehicle set anywhere
  // (homepage, header, PDP) auto-filters every collection to fitting parts — and
  // a filter applied on a collection populates the garage so the PDP badge is
  // already answered when you open a product (no re-submit).
  var FIT_PREFIX = 'filter.p.m.custom.fitment_';

  function onCollectionPage() {
    return /^\/collections\//.test(location.pathname) && location.pathname.indexOf('/products/') === -1;
  }
  function urlHasFitFilter() { return location.search.indexOf(FIT_PREFIX) !== -1; }
  function showAllKey() { return 'cpg:showall:' + location.pathname; }
  function isShowAll() { try { return sessionStorage.getItem(showAllKey()) === '1'; } catch (e) { return false; } }
  function setShowAll(on) { try { on ? sessionStorage.setItem(showAllKey(), '1') : sessionStorage.removeItem(showAllKey()); } catch (e) {} }

  function stripFit(q) {
    var ks = []; q.forEach(function (_v, k) { if (k.indexOf(FIT_PREFIX) === 0) ks.push(k); });
    ks.forEach(function (k) { q.delete(k); });
  }
  function applyFit(v) {
    // Collection filtering is Year/Make/Model only — the submodel/engine list
    // metafields are sparse (many parts are trim-agnostic, and lists cap at 128),
    // so ANDing them in would wrongly hide fitting parts. Submodel/engine precision
    // lives on the PDP verify (full per-row data), not the coarse collection filter.
    var q = new URLSearchParams(location.search);
    stripFit(q);
    q.set(FIT_PREFIX + 'year', v.year);
    q.set(FIT_PREFIX + 'make', v.make);
    q.set(FIT_PREFIX + 'model', v.model);
    location.replace(location.pathname + '?' + q.toString());
  }
  function clearFit() {
    var q = new URLSearchParams(location.search);
    stripFit(q);
    setShowAll(true);
    var qs = q.toString();
    location.assign(location.pathname + (qs ? '?' + qs : ''));
  }
  function vehicleFromUrl() {
    var q = new URLSearchParams(location.search);
    var y = q.get(FIT_PREFIX + 'year'), mk = q.get(FIT_PREFIX + 'make'), md = q.get(FIT_PREFIX + 'model');
    if (!y || !mk || !md) return null;
    var v = { year: parseInt(y, 10), make: mk, model: md, submodel: q.get(FIT_PREFIX + 'submodel') || null, engine: null };
    v.label = vehicleLabel(v); return v;
  }
  /** True when the URL's fitment filter already targets this exact vehicle — used
   *  to avoid a redundant reload (and the reverse-sync → change-event loop). */
  function fitMatchesUrl(v) {
    var u = vehicleFromUrl();
    if (!u || !v) return false;
    // Compare only what the collection filter actually applies (Year/Make/Model).
    return String(u.year) === String(v.year) && u.make === v.make && u.model === v.model;
  }

  function removeFitBanner() {
    var b = document.querySelector('[data-cpg-fit-banner]');
    if (b) b.parentNode.removeChild(b);
  }
  var FIT_CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  var FIT_CAR_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 17h14M5 17a2 2 0 1 1-4 0M19 17a2 2 0 1 0 4 0M2 13l2-5a2 2 0 0 1 2-1h12a2 2 0 0 1 2 1l2 5M2 13h20"></path></svg>';
  var FIT_X_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
  /** Owner mockup fitment banner (2026-07-24): icon + label + vehicle +
   *  "Change Vehicle" (opens garage) + a contextual secondary action.
   *  No part-count shown - native collection pages cannot reliably supply
   *  both fit/total, and a made-up count would violate the fitment rule. */
  function fitBannerHTML(v, label, secondary) {
    return '<span class="cpg-fit-banner__icon">' + FIT_CHECK_SVG + '</span>' +
      '<span class="cpg-fit-banner__info"><span class="cpg-fit-banner__label">' + label + '</span>' +
      '<span class="cpg-fit-banner__veh">' + esc(v.label) + '</span></span>' +
      '<span class="cpg-fit-banner__actions">' +
        '<button type="button" class="cpg-fit-banner__change" data-cpg-garage-button aria-label="Change vehicle">' + FIT_CAR_SVG + '<span class="cpg-fit-banner__change-txt">Change Vehicle</span></button>' +
        secondary +
      '</span>';
  }
  function renderFitBanner(state, v) {
    // BANNER REMOVED ON DUP (2026-08-16): this used to inject a second ".cpg-fit-banner"
    // ("Fitment Filter Active" / "Change Vehicle" / "Clear") that DUPLICATED the sitewide
    // cpg-vehicle-bar (.cpg-vbar, "Shopping for"). The sitewide bar is the single fitment
    // bar now, so this injector is a no-op. Kept (not deleted) so every caller stays valid.
    removeFitBanner();
  }

  /** Shopify silently ignores S&D filters on collections >5k products — fit
   *  params on these pages do nothing, so never pretend to filter there. */
  function onUnfilterableCollection() { return location.pathname === '/collections/all'; }

  /** Curated collections (Best Sellers etc., template suffix "curated") are
   *  hand-picked lists, not fitment-scoped catalogs — auto-applying the garage
   *  filter empties them because their products rarely carry the exact
   *  fitment metafields. The section marks itself with this attribute. */
  function onCuratedCollection() { return !!document.querySelector('[data-cpg-curated-collection]'); }

  /** The Shop All section (brand / category / bestseller collection pages)
   *  scopes to the garage vehicle ITSELF, reading localStorage directly and
   *  re-rendering in place. Applying the URL fitment filter here would fight
   *  its writeUrl() — the section strips the fit params on every render, and
   *  this re-adds them, producing a reload loop. So when Shop All owns the
   *  page, leave collection filtering entirely to it. */
  function onShopAllPage() { return !!document.querySelector('[id^="cpg-sa-"]'); }

  /** The cpg-collection sections (category collection pages AND the vehicle pages)
   *  re-render the grid IN PLACE from the catalog API on every garage change, exactly
   *  like Shop All. Applying the Shopify native fitment filter here forces a full
   *  server-side page reload (6-7s) that the client-side render makes redundant — and
   *  on the membership-less vehicle pages the filter matches nothing. So leave all
   *  fitment filtering to the section: no applyFit, no reload. */
  function onCpgCollection() { return !!document.querySelector('.cpg-collection[data-collection-id]'); }

  function syncCollectionFilter() {
    if (!onCollectionPage()) return;
    if (onShopAllPage() || onCpgCollection()) { removeFitBanner(); return; }
    var active = readGarage();
    if (onCuratedCollection() && urlHasFitFilter()) {
      var cq = new URLSearchParams(location.search);
      stripFit(cq);
      var cqs = cq.toString();
      location.replace(location.pathname + (cqs ? '?' + cqs : ''));
      return;
    }
    if (onUnfilterableCollection() || onCuratedCollection()) {
      // Banner removed on DUP (see renderFitBanner). No second fitment bar here either.
      removeFitBanner();
      return;
    }
    if (urlHasFitFilter()) {
      var urlV = vehicleFromUrl();
      // NOTE: no reverse-sync into the garage here. Writing the URL vehicle to
      // the garage resurrected vehicles the shopper had just removed (stale
      // "Shopping for X" header). The banner still shows the URL vehicle.
      renderFitBanner('filtered', active || urlV);
      return;
    }
    if (active && !isShowAll()) { applyFit(active); return; } // auto-filter to the garage vehicle
    if (active && isShowAll()) { renderFitBanner('offer', active); }
  }

  // ── dup-theme nav rewrite ──────────────────────────────────────────────────
  // "Shop All" targets the native catalog browser. Done theme-side (this asset
  // ships only with the teardown theme) because Shopify nav MENUS are
  // store-wide — editing the menu itself would repoint the LIVE header too.
  // At cutover the real menu link changes and this becomes a no-op.
  function rewriteShopAllLinks() {
    document.querySelectorAll('a[href="/collections/all"], a[href^="/collections/all?"]').forEach(function (a) {
      a.setAttribute('href', '/pages/shop-all');
    });
    // The nav menu's "Shop All" item points at /pages/search (Convermax-era) —
    // match it by label since other links may legitimately target that page.
    document.querySelectorAll('a').forEach(function (a) {
      if (a.textContent.trim() === 'Shop All') a.setAttribute('href', '/pages/shop-all');
    });
  }

  // ── boot ───────────────────────────────────────────────────────────────────
  function boot() {
    injectCss();
    document.querySelectorAll('[data-cpg-ymm-widget]').forEach(initHost);
    decorateGarageButtons();
    syncCollectionFilter();
    rewriteShopAllLinks();
    // Menus render lazily (mega menu, mobile drawer clones) — sweep again.
    [400, 1200, 2500].forEach(function (d) { window.setTimeout(rewriteShopAllLinks, d); });
    // On a collection page, re-filter when the active vehicle changes.
    window.addEventListener('cpg:vehicle-changed', function () {
      if (!onCollectionPage()) return;
      if (onCpgCollection()) return; // section re-renders in place; never trigger the slow applyFit reload
      if (onUnfilterableCollection()) { syncCollectionFilter(); return; }
      var active = readGarage();
      if (active) {
        if (fitMatchesUrl(active)) return; // already showing this vehicle — no reload (also breaks the reverse-sync loop)
        setShowAll(false);
        applyFit(active);
      } else if (urlHasFitFilter()) {
        clearFit();
      }
    });
    // Back/forward-cache restores skip boot, so a page restored via the back
    // button could show a stale garage chip or a fit banner for a vehicle that
    // was removed on another page. Re-sync from localStorage on bfcache restore.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) fireChanged();
    });
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();

/* Sitewide cart-sync (2026-08-17): keep the top-bar cart count + total in agreement with the
   real cart after ANY mutation — add, quantity change, remove, clear — whether it comes from a
   product card, the PDP form, or inside the cart drawer. It only writes the leaf count/total
   spans (.cpg-tb__cart-n / .cpg-tb-m__badge / .cp-cart-count and .cpg-tb__cart-total); it never
   touches .cpg-tb-m__cart or .cpg-tb__cart-ic, which hold the cart SVG icon. */
(function () {
  if (window.__cpgCartSyncInstalled) return;
  window.__cpgCartSyncInstalled = true;
  function fmtMoney(cents) {
    try { return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  var pending = null;
  function refresh() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      fetch(location.origin + '/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var n = c.item_count, tot = fmtMoney(c.total_price);
          document.querySelectorAll('.cpg-tb__cart-n, .cpg-tb-m__badge, .cp-cart-count').forEach(function (el) { el.textContent = n; });
          document.querySelectorAll('.cpg-tb__cart-total').forEach(function (el) { el.textContent = tot; });
        })
        .catch(function () {});
    }, 70);
  }
  window.cpgRefreshHeaderCart = refresh;
  var CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  // fetch
  var _fetch = window.fetch;
  if (typeof _fetch === 'function' && !_fetch.__cpgCartWrapped) {
    var wrapped = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var p = _fetch.apply(this, arguments);
      if (CART_RE.test(url)) { try { p.then(function () { refresh(); }); } catch (e) {} }
      return p;
    };
    wrapped.__cpgCartWrapped = true;
    try { window.fetch = wrapped; } catch (e) {}
  }
  // XMLHttpRequest (the theme drawer's remove/qty may use XHR)
  var XP = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (XP && !XP.__cpgCartWrapped) {
    var _open = XP.open;
    XP.open = function (method, url) { try { this.__cpgUrl = url; } catch (e) {} return _open.apply(this, arguments); };
    var _send = XP.send;
    XP.send = function () {
      try {
        if (this.__cpgUrl && CART_RE.test(this.__cpgUrl)) {
          this.addEventListener('load', function () { refresh(); });
        }
      } catch (e) {}
      return _send.apply(this, arguments);
    };
    XP.__cpgCartWrapped = true;
  }
  if (document.readyState !== 'loading') refresh();
  else document.addEventListener('DOMContentLoaded', refresh);
})();
